import { useMemo, useState } from "react";
import AttachmentManager from "./AttachmentManager.jsx";

const blankPlan = {
  id: "",
  operationId: "",
  targetName: "",
  targetDob: "",
  caseNumber: "",
  operationType: "Search Warrant",
  address: "",
  charges: "",
  knownWeapons: "",
  threatAssessment: "",
  criminalHistory: "",
  occupants: "",
  children: "",
  animals: "",
  vehicles: "",
  entryPlan: "",
  containmentPlan: "",
  arrestTeam: "",
  perimeterAssignments: "",
  lessLethalPlan: "",
  medicalPlan: "",
  commsPlan: "",
  staging: "",
  routeIn: "",
  routeOut: "",
  evidencePlan: "",
  specialInstructions: "",
  legalAuthority: "",
  notes: "",
  status: "Draft",
  attachments: []
};

function makeId(prefix = "plan") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function OperationsPlanning({ operations, operationsPlans, setOperationsPlans, onOpenOperation }) {
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(blankPlan);

  const selectedPlan = useMemo(
    () => operationsPlans.find((p) => p.id === selectedId) || null,
    [selectedId, operationsPlans]
  );

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function startNew() {
    setSelectedId("");
    setForm(blankPlan);
  }

  function editPlan(plan) {
    setSelectedId(plan.id);
    setForm({ ...blankPlan, ...plan, attachments: Array.isArray(plan.attachments) ? plan.attachments : [] });
  }

  function savePlan(e) {
    e.preventDefault();
    if (!form.operationId) {
      alert("Link this plan to an operation first.");
      return;
    }

    const now = new Date().toISOString();
    const plan = {
      ...form,
      id: form.id || makeId(),
      updatedAt: now,
      createdAt: form.createdAt || now,
      attachments: Array.isArray(form.attachments) ? form.attachments : []
    };

    const exists = operationsPlans.some((p) => p.id === plan.id);
    const next = exists
      ? operationsPlans.map((p) => (p.id === plan.id ? plan : p))
      : [plan, ...operationsPlans];

    setOperationsPlans(next);
    setSelectedId(plan.id);
    setForm(plan);
  }

  function deletePlan(id) {
    if (!confirm("Delete this operations plan?")) return;
    setOperationsPlans(operationsPlans.filter((p) => p.id !== id));
    if (selectedId === id) startNew();
  }

  function exportPlan(plan) {
    const operation = operations.find((op) => op.id === plan.operationId);
    const esc = (value) =>
      String(value || "").replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]));
    const win = window.open("", "_blank");
    if (!win) return alert("Popup blocked. Allow popups and try again.");
    win.document.write(`
      <html><head><title>TacOps Operations Plan</title>
      <style>body{font-family:Arial,sans-serif;margin:36px;color:#111}h1{margin-bottom:4px}h2{border-bottom:1px solid #999;padding-bottom:4px;margin-top:22px}.sub{color:#555;margin-bottom:20px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.box{border:1px solid #ccc;padding:10px;margin:8px 0;page-break-inside:avoid}.label{font-size:11px;color:#555;text-transform:uppercase;font-weight:bold}.value{margin-top:4px;white-space:pre-wrap;line-height:1.35}@media print{button{display:none}}</style>
      </head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <h1>TacOps Operations Planning Packet</h1><div class="sub">TacOps by Aegis Group${operation ? " • Linked Operation: " + esc(operation.title) : ""}</div>
      <div class="grid">
        <div class="box"><div class="label">Operation</div><div class="value">${esc(operation?.title)}</div></div>
        <div class="box"><div class="label">Operation Type</div><div class="value">${esc(plan.operationType || operation?.operationType)}</div></div>
        <div class="box"><div class="label">Target / Subject</div><div class="value">${esc(plan.targetName)}</div></div>
        <div class="box"><div class="label">Location</div><div class="value">${esc(plan.address || operation?.location)}</div></div>
        <div class="box"><div class="label">Case Number</div><div class="value">${esc(plan.caseNumber)}</div></div>
        <div class="box"><div class="label">Status</div><div class="value">${esc(plan.status)}</div></div>
      </div>
      <h2>Objective / Legal</h2><div class="box"><div class="label">Charges / Offenses / Purpose</div><div class="value">${esc(plan.charges)}</div></div><div class="box"><div class="label">Legal Authority</div><div class="value">${esc(plan.legalAuthority)}</div></div>
      <h2>Threat Assessment</h2><div class="box"><div class="label">Known Weapons</div><div class="value">${esc(plan.knownWeapons)}</div></div><div class="box"><div class="label">Threat Assessment</div><div class="value">${esc(plan.threatAssessment)}</div></div><div class="box"><div class="label">Criminal History / Safety Notes</div><div class="value">${esc(plan.criminalHistory)}</div></div>
      <h2>Location / Occupants</h2><div class="grid"><div class="box"><div class="label">Occupants</div><div class="value">${esc(plan.occupants)}</div></div><div class="box"><div class="label">Children</div><div class="value">${esc(plan.children)}</div></div><div class="box"><div class="label">Animals</div><div class="value">${esc(plan.animals)}</div></div><div class="box"><div class="label">Vehicles</div><div class="value">${esc(plan.vehicles)}</div></div></div>
      <h2>Tactical Plan</h2><div class="box"><div class="label">Entry / Action Plan</div><div class="value">${esc(plan.entryPlan)}</div></div><div class="box"><div class="label">Containment Plan</div><div class="value">${esc(plan.containmentPlan)}</div></div><div class="box"><div class="label">Assignments</div><div class="value">${esc(plan.arrestTeam)}\n${esc(plan.perimeterAssignments)}</div></div><div class="box"><div class="label">Less-Lethal Plan</div><div class="value">${esc(plan.lessLethalPlan)}</div></div>
      <h2>Support Plans</h2><div class="box"><div class="label">Medical Plan</div><div class="value">${esc(plan.medicalPlan)}</div></div><div class="box"><div class="label">Comms Plan</div><div class="value">${esc(plan.commsPlan)}</div></div><div class="box"><div class="label">Evidence / Documentation Plan</div><div class="value">${esc(plan.evidencePlan)}</div></div>
      <h2>Staging / Movement</h2><div class="box"><div class="label">Staging</div><div class="value">${esc(plan.staging)}</div></div><div class="box"><div class="label">Route In</div><div class="value">${esc(plan.routeIn)}</div></div><div class="box"><div class="label">Route Out</div><div class="value">${esc(plan.routeOut)}</div></div>
      <h2>Attachments</h2><div class="box"><div class="value">${(plan.attachments || []).map((a) => esc(a.name)).join("\\n") || "No attachments listed."}</div></div>
      <h2>Special Instructions / Notes</h2><div class="box"><div class="value">${esc(plan.specialInstructions)}</div></div><div class="box"><div class="value">${esc(plan.notes)}</div></div>
      </body></html>`);
    win.document.close();
  }

  return (
    <section className="warrant-layout">
      <div>
        <div className="section-head">
          <div>
            <h2>Operations Planning</h2>
            <p className="muted">Structured operational planning for tactical deployments, surveillance, warrant service, barricades, recon, and specialty missions.</p>
          </div>
          <button className="primary" onClick={startNew}>Add Operations Plan</button>
        </div>

        <div className="mission-list">
          {operationsPlans.length === 0 ? <div className="empty">No operations plans yet. Create the first plan.</div> : null}
          {operationsPlans.map((plan) => {
            const operation = operations.find((op) => op.id === plan.operationId);
            return (
              <div className={`mission-card priority-${plan.status === "Approved" ? "high" : "normal"}`} key={plan.id}>
                <div className="mission-main">
                  <div className="mission-title">{operation?.title || plan.targetName || plan.address || "Untitled Operations Plan"}</div>
                  <div className="mission-meta">{plan.operationType} • {plan.address || operation?.location || "No location"} • {plan.status}</div>
                  <p>{plan.threatAssessment || plan.entryPlan || "No threat or action plan entered."}</p>
                  <div className="chips">
                    <span className="badge neutral">{plan.status}</span>
                    {operation ? <span className="badge neutral">Linked: {operation.title}</span> : null}
                    {(plan.attachments || []).length ? <span className="badge neutral">{plan.attachments.length} attachments</span> : null}
                  </div>
                </div>
                <div className="mission-actions">
                  <button onClick={() => editPlan(plan)}>Edit</button>
                  <button onClick={() => exportPlan(plan)}>Export Plan</button>
                  {operation ? <button onClick={() => onOpenOperation(operation)}>Open Operation</button> : null}
                  <button className="danger-text" onClick={() => deletePlan(plan.id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <form className="panel warrant-form" onSubmit={savePlan}>
        <div className="section-head">
          <div>
            <h2>{selectedPlan ? "Edit Operations Plan" : "Add Operations Plan"}</h2>
            <p className="muted">Operations plans attach to an operation and feed packets, maps, timelines, and AARs.</p>
          </div>
        </div>

        <div className="form-grid">
          <Select label="Linked Operation" value={form.operationId} onChange={(v) => update("operationId", v)} options={[["", "Select Operation"], ...operations.map((op) => [op.id, op.title])]} pair />
          <Select label="Operation Type" value={form.operationType} onChange={(v) => update("operationType", v)} options={["Search Warrant", "Arrest Warrant", "GPS Placement", "Surveillance", "Barricaded Subject", "Dignitary / Security", "Reconnaissance", "High-Risk Apprehension", "Search Operation", "Containment", "Rural Tracking", "Agency Assist", "Other"]} />
          <Field label="Target / Subject" value={form.targetName} onChange={(v) => update("targetName", v)} />
          <Field label="Target DOB" value={form.targetDob} onChange={(v) => update("targetDob", v)} />
          <Field label="Case Number" value={form.caseNumber} onChange={(v) => update("caseNumber", v)} />
          <Select label="Status" value={form.status} onChange={(v) => update("status", v)} options={["Draft", "Planning", "Ready for Briefing", "Approved", "Executed", "Archived"]} />
        </div>

        <Field label="Operation Address / Location" value={form.address} onChange={(v) => update("address", v)} full />
        <Text label="Charges / Offenses / Purpose" value={form.charges} onChange={(v) => update("charges", v)} />
        <Text label="Legal Authority" value={form.legalAuthority} onChange={(v) => update("legalAuthority", v)} />
        <Text label="Known Weapons" value={form.knownWeapons} onChange={(v) => update("knownWeapons", v)} />
        <Text label="Threat Assessment" value={form.threatAssessment} onChange={(v) => update("threatAssessment", v)} />
        <Text label="Criminal History / Officer Safety Notes" value={form.criminalHistory} onChange={(v) => update("criminalHistory", v)} />

        <div className="form-grid">
          <Text label="Occupants" value={form.occupants} onChange={(v) => update("occupants", v)} />
          <Text label="Children" value={form.children} onChange={(v) => update("children", v)} />
          <Text label="Animals" value={form.animals} onChange={(v) => update("animals", v)} />
          <Text label="Vehicles" value={form.vehicles} onChange={(v) => update("vehicles", v)} />
        </div>

        <Text label="Entry / Action Plan" value={form.entryPlan} onChange={(v) => update("entryPlan", v)} />
        <Text label="Containment Plan" value={form.containmentPlan} onChange={(v) => update("containmentPlan", v)} />
        <Text label="Entry / Arrest / Action Team" value={form.arrestTeam} onChange={(v) => update("arrestTeam", v)} />
        <Text label="Perimeter Assignments" value={form.perimeterAssignments} onChange={(v) => update("perimeterAssignments", v)} />
        <Text label="Less-Lethal Plan" value={form.lessLethalPlan} onChange={(v) => update("lessLethalPlan", v)} />
        <Text label="Medical Plan / EMS Staging" value={form.medicalPlan} onChange={(v) => update("medicalPlan", v)} />
        <Text label="Communications Plan" value={form.commsPlan} onChange={(v) => update("commsPlan", v)} />
        <Text label="Evidence / Documentation Plan" value={form.evidencePlan} onChange={(v) => update("evidencePlan", v)} />

        <div className="form-grid">
          <Text label="Staging" value={form.staging} onChange={(v) => update("staging", v)} />
          <Text label="Route In" value={form.routeIn} onChange={(v) => update("routeIn", v)} />
          <Text label="Route Out" value={form.routeOut} onChange={(v) => update("routeOut", v)} />
          <Text label="Special Instructions" value={form.specialInstructions} onChange={(v) => update("specialInstructions", v)} />
        </div>

        <Text label="Notes" value={form.notes} onChange={(v) => update("notes", v)} />

        <AttachmentManager
          label="Operations Plan Attachments / Photos / PDFs"
          attachments={form.attachments}
          onChange={(next) => update("attachments", next)}
        />

        <div className="modal-actions">
          <button type="button" onClick={startNew}>Clear</button>
          <button className="primary" type="submit">Save Operations Plan</button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", full = false }) {
  return (
    <label className={full ? "full" : ""}>
      <span>{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Select({ label, value, onChange, options, pair = false }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {options.map((x) => {
          const key = pair ? x[0] : x;
          const label = pair ? x[1] : x;
          return <option key={key} value={key}>{label}</option>;
        })}
      </select>
    </label>
  );
}

function Text({ label, value, onChange }) {
  return (
    <label className="full">
      <span>{label}</span>
      <textarea rows="3" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
